/**
 * ProtectedRoute Component
 *
 * A route guard that protects routes from unauthenticated access.
 * Redirects unauthenticated users to the login page while preserving
 * the intended destination for post-login redirect.
 *
 * Features:
 * - Shows loading spinner during auth initialization
 * - Preserves intended destination in location.state.from
 * - Uses Outlet for nested route rendering
 * - Prevents flash of unauthorized content
 * - Redirects users with forcePasswordChange to change password page
 */

import { type FC } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useAuthInitialized, useAuthLoading } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { PageLoadingSpinner } from '@/components/common/LoadingSpinner';
import { useLanguage } from '@/hooks/useLanguage';

interface ProtectedRouteProps {
  /** Optional fallback component while loading */
  fallback?: React.ReactNode;
  /** Path to redirect unauthenticated users (defaults to /login) */
  loginPath?: string;
  /** Path to redirect users who must change password (defaults to /auth/change-password) */
  changePasswordPath?: string;
}

/**
 * ProtectedRoute guards routes that require authentication.
 *
 * @example
 * // Basic usage with Outlet
 * <Route element={<ProtectedRoute />}>
 *   <Route path="dashboard" element={<Dashboard />} />
 *   <Route path="profile" element={<Profile />} />
 * </Route>
 *
 * @example
 * // With custom login path
 * <Route element={<ProtectedRoute loginPath="/auth/login" />}>
 *   <Route path="admin/*" element={<AdminRoutes />} />
 * </Route>
 */
export const ProtectedRoute: FC<ProtectedRouteProps> = ({
  fallback,
  loginPath = '/login',
  changePasswordPath = '/auth/change-password',
}) => {
  const location = useLocation();
  const { t } = useLanguage();
  const isAuthenticated = useIsAuthenticated();
  const isInitialized = useAuthInitialized();
  const isLoading = useAuthLoading();
  const forcePasswordChange = useAuthStore((state) => state.forcePasswordChange);

  // Show loading state while auth is initializing
  // This prevents flash of login page for users with valid sessions
  if (!isInitialized || isLoading) {
    return fallback ?? (
      <div className="min-h-screen flex items-center justify-center bg-surface-50">
        <PageLoadingSpinner
          variant="primary"
          label={t('common.loading')}
        />
      </div>
    );
  }

  // Redirect to login if not authenticated
  // Preserve the current location so we can redirect back after login
  if (!isAuthenticated) {
    return (
      <Navigate
        to={loginPath}
        state={{ from: location }}
        replace
      />
    );
  }

  // Redirect to change password if user must change their password
  // IMPORTANT: Allow access to the change password page itself to avoid redirect loop
  if (forcePasswordChange && location.pathname !== changePasswordPath) {
    return (
      <Navigate
        to={changePasswordPath}
        replace
      />
    );
  }

  // User is authenticated - render child routes
  return <Outlet />;
};

export default ProtectedRoute;
